// v2.71.83 — Tiny inline ErrorBoundary for individual page sections.
//
// Difference from `<PageErrorBoundary>` (v2.71.77):
//   • PageErrorBoundary wraps an entire ROUTE — when it catches, the whole
//     page is replaced with the friendly "Something went wrong" card.
//   • SectionBoundary wraps a single SECTION of a page (gallery, EMI calc,
//     mini-map, similar properties, notes…) — when it catches, only THAT
//     section is hidden + a tiny inline "couldn't load" pill is shown,
//     and the rest of the page keeps rendering normally.
//
// Why this matters
// ────────────────
// Production users were reporting full-page "Something went wrong" on
// PropertyDetail. Whatever the underlying crash is (could be a single
// malformed property field, a third-party widget, a Leaflet tile error,
// a Pillow-resized image url that 500s, etc.), the result was that one
// localised error killed the whole page. With section-level boundaries
// the user can still read all the OTHER sections — title, price, BHK,
// status, contact — even if one section (say the map or similar
// properties) crashes.
//
// Best-practice notes
// ───────────────────
//   • Console-loud (eslint-disable no-console) so devs can still diagnose.
//   • Single-line inline fallback — no big card, no scary copy. Section
//     just quietly disappears with a faint label that something's wrong.
//   • `silent` opt → hide fallback entirely (use for purely cosmetic
//     sections where even the pill would be noise).
//   • Reuses the same React class pattern as ErrorBoundary.jsx; no extra
//     dependency.

import React from "react";

export default class SectionBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[SectionBoundary:${this.props.name || "anon"}]`,
      error,
      info?.componentStack
    );
  }
  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.silent) return null;
    return (
      <div
        className="my-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-800"
        data-testid={`section-boundary-${this.props.name || "anon"}`}
        role="alert"
      >
        Couldn’t load this section. The rest of the page is unaffected.
      </div>
    );
  }
}
